'use client';
import { useEffect, useRef } from 'react';
import { CustomerKeyType } from '../../constants';
import { useWishlistCustomerKey } from '../../context';
import { useWishlistService } from './useWishlistService';

/**
 * Reunites a pre-login guest wishlist with the account that now owns it.
 *
 * Mount once per app. Without this, items added before signing in stay under the guest key and
 * disappear from every account-scoped surface.
 */
export const useWishlistGuestMigration = () => {
  const { customerKey: storedCustomerKey, isKeyResolved, customerId, clearCustomerKey } = useWishlistCustomerKey();
  const { useMigrateWishlist } = useWishlistService();
  const { mutate: migrateWishlist } = useMigrateWishlist();

  const migratedGuestKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isKeyResolved || !customerId || !storedCustomerKey) {
      return;
    }

    if (storedCustomerKey === customerId || migratedGuestKeyRef.current === storedCustomerKey) {
      return;
    }

    migratedGuestKeyRef.current = storedCustomerKey;

    migrateWishlist(
      {
        fromCustomerKey: storedCustomerKey,
        fromCustomerKeyType: CustomerKeyType.GUEST,
        toCustomerKey: customerId,
        toCustomerKeyType: CustomerKeyType.CUSTOMER,
      },
      {
        onSuccess: () => {
          // Drop the guest key rather than pointing it at the account. Storage is keyed only by
          // customerKey, so an account id left here would make the next signed-out visitor on this
          // device read and write that account's wishlist.
          clearCustomerKey();
        },
        onError: () => {
          // Release the guard so a later mount can retry instead of stranding the guest wishlist.
          migratedGuestKeyRef.current = undefined;
        },
      },
    );
  }, [isKeyResolved, customerId, storedCustomerKey, migrateWishlist, clearCustomerKey]);
};
